import React from 'react';
import {
    TbArrowsDoubleNeSw,
    TbBattery2,
    TbBolt,
    TbBroadcast,
    TbClock,
    TbCloud,
    TbCurrencyDollar,
    TbEdit,
    TbListSearch,
    TbMathMaxMin,
    TbMessage,
    TbMessageCircle,
    TbPointer,
    TbShieldCheck,
} from 'react-icons/tb';

import { GridColProps, MantineColor } from '@mantine/core';

import { AnimatedExamplesBox } from '@marketing/components/animated-examples/AnimatedExamplesBox';
import { SUPPORT_EXAMPLE_QUESTIONS } from '@diagram/components/diagram-sidebar/sidebars/chat/components/support-example-questions';

import { URLHelpers } from 'helpers/URLHelpers';

import { Gradient } from '@/../marketing/helpers/gradient';

type FeatureKey =
    | 'annotations'
    | 'batteryCharging'
    | 'billOfMaterials'
    | 'cloudBased'
    | 'faultSimulation'
    | 'inAppCommunication'
    | 'inAppSupport'
    | 'inAppSupportOnDemand'
    | 'inAppSupportContextual'
    | 'liveData'
    | 'powerFlowSimulation'
    | 'productCatalog'
    | 'prototyping'
    | 'realtimeCollaboration'
    | 'validations'
    | 'wireSizing';

export type Feature = {
    title: string;
    header?: React.ReactNode;
    description: React.ReactNode;
    features?: React.ReactNode[];
    icon?: React.ReactNode;
    badge?: string;
    action?: {
        label: string;
        href: string;
    };
    image?: {
        src: string;
        width: number;
        height: number;
    };
    preview?:
        | {
              type: 'video';
              sources: Array<{
                  src: string;
                  type: string;
              }>;
              fit?: 'cover' | 'contain';
          }
        | {
              type: 'image';
              image: {
                  src: string;
                  width: number;
                  height: number;
              };
              fit?: 'cover' | 'contain';
          }
        | {
              type: 'scrolling-image';
              image: {
                  src: string;
                  width: number;
                  height: number;
              };
          }
        | {
              type: 'text';
              text: React.ReactNode;
          }
        | {
              type: 'html';
              html: React.ReactNode;
          };
    gradient?: Gradient;
    variant?: 'default' | 'light' | 'dark';
    color?: MantineColor;
    orientation?: 'horizontal' | 'vertical';
    span?: GridColProps['span'];
};

import annotationsImage from '../../../app/(app)/design-software/features/annotations/annotations.png';
import validationsImage from '../../../app/(app)/design-software/features/validations/validations.png';
import wireSizingsImage from '../../../app/(app)/design-software/features/wire-sizing/wire-sizing.png';

const features: Record<FeatureKey, Feature> = {
    annotations: {
        title: 'Annotations',
        description: (
            <>
                Mark up your single line with logos, equipment photos, groups and notes - keep context next to the
                topology.
            </>
        ),
        icon: <TbEdit strokeWidth={1.25} />,
        // action: {
        //     label: 'Learn more',
        //     href: '/design-software/features/annotations',
        // },
        preview: {
            type: 'image',
            image: annotationsImage,
        },
        gradient: 'orange',
    },
    // arcFlash: {
    //     title: 'Arc Flash Analysis',
    //     description: <>Estimate incident energy and arc-flash boundaries on your DC buses to inform PPE selection.</>,
    //     icon: <TbFlame strokeWidth={1.25} />,
    //     action: {
    //         label: 'Learn more',
    //         href: '/design-software/features/arc-flash',
    //     },
    //     preview: {
    //         type: 'video',
    //         sources: [{ src: URLHelpers.file('/videos/features/arc-flash.mp4'), type: 'video/mp4' }],
    //     },
    //     gradient: 'orange',
    //     badge: 'Alpha',
    // },
    batteryCharging: {
        title: 'Battery (dis)charging',
        description: <>Simulate how batteries charge and discharge over time.</>,
        icon: <TbBattery2 strokeWidth={1.25} />,
        // action: {
        //     label: 'Learn more',
        //     href: '/design-software/features/battery-charging',
        // },
        gradient: 'red',
    },
    billOfMaterials: {
        title: 'Bill of Materials',
        header: <>From Schematic to Valid&nbsp;BOM</>,
        description: (
            <>
                Every component validated, every cable sized, every connection checked. Go from first sketch to a
                procurement-ready bill of materials - in hours, not weeks.
            </>
        ),
        icon: <TbCurrencyDollar strokeWidth={1.25} />,
        // action: {
        //     label: 'Learn more',
        //     href: '/design-software/features/bill-of-materials',
        // },
        gradient: 'pink',
        span: { base: 12 },
        orientation: 'horizontal' as const,
    },
    cloudBased: {
        title: 'Cloud-Based',
        description: (
            <>
                Our tool is web-based and accessible from any device with a browser. Create a profile and get started in
                minutes.
            </>
        ),
        icon: <TbCloud strokeWidth={1.25} />,
        // action: {
        //     label: 'Learn more',
        //     href: '/design-software/features/cloud-based',
        // },
        gradient: 'green',
    },
    faultSimulation: {
        title: 'Fault Simulation',
        description: <>Run short-circuit scenarios across your DC system and find the perfect protection devices.</>,
        icon: <TbBolt strokeWidth={1.25} />,
        // action: {
        //     label: 'Learn more',
        //     href: '/design-software/features/fault-simulation',
        // },
        preview: {
            type: 'video',
            sources: [
                { src: URLHelpers.file('/videos/features/fault-sim.webm'), type: 'video/webm' },
                { src: URLHelpers.file('/videos/features/fault-sim.mp4'), type: 'video/mp4' },
            ],
        },
        gradient: 'red',
    },
    inAppCommunication: {
        title: 'Chat Channels',
        description: (
            <>
                Say goodbye to scattered communication across emails, messages, and meetings. All discussions are stored
                with your design, creating a complete record of decisions and feedback.
            </>
        ),
        icon: <TbMessageCircle strokeWidth={1.25} />,
        // action: {
        //     label: 'Learn more',
        //     href: '/design-software/features/in-app-communication',
        // },
        gradient: 'purple',
        preview: {
            type: 'video',
            sources: [
                { src: URLHelpers.file('/videos/features/contextual-discussions.webm'), type: 'video/webm' },
                { src: URLHelpers.file('/videos/features/contextual-discussions.mp4'), type: 'video/mp4' },
            ],
            fit: 'contain',
        },
    },
    inAppSupport: {
        title: 'DC Expert Support',
        description: (
            <>
                Ask a DC expert directly in your project. Get answers from engineers who've designed, simulated, and
                commissioned real DC power systems.
            </>
        ),
        features: [
            <>
                <strong>Ask a DC expert inside your design</strong> - get answers from engineers who've commissioned
                real DC power systems, without leaving your project or scheduling a separate call.
            </>,
            <>
                <strong>Context-aware support</strong> - share the exact part of your design you're stuck on; experts
                and manufacturer technical teams can see what you're working on and give targeted, precise guidance.
            </>,
        ],
        preview: {
            type: 'video',
            sources: [
                { src: URLHelpers.file('/videos/features/expert-support.webm'), type: 'video/webm' },
                { src: URLHelpers.file('/videos/features/expert-support.mp4'), type: 'video/mp4' },
            ],
        },
        icon: <TbMessage strokeWidth={1.25} />,
    },
    inAppSupportOnDemand: {
        title: 'Expert Assistance On Demand',
        description: (
            <>
                Connect with engineering and product experts directly within your design environment, eliminating the
                need to switch between applications or schedule separate meetings.
                <br />
                <br />
                Get specialized help exactly when you need it, whether you&apos;re facing a technical challenge, need
                design validation, or have questions about specific components.
            </>
        ),
        icon: <TbMessage strokeWidth={1.25} />,
        preview: {
            type: 'video',
            sources: [
                { src: URLHelpers.file('/videos/features/expert-support.webm'), type: 'video/webm' },
                { src: URLHelpers.file('/videos/features/expert-support.mp4'), type: 'video/mp4' },
            ],
        },
    },
    inAppSupportContextual: {
        title: 'Contextual Problem Solving',
        description: (
            <>
                Share specific parts of your design with support engineers who can see exactly what you&apos;re working
                on, providing targeted advice and solutions for your unique challenges.
                <br />
                <br />
                Support staff can highlight areas of concern, suggest alternatives, or provide guidance directly on your
                design, making complex technical discussions clear and precise.
            </>
        ),
        icon: <TbMessage strokeWidth={1.25} />,
        preview: {
            type: 'html',
            html: <AnimatedExamplesBox replay examples={SUPPORT_EXAMPLE_QUESTIONS} />,
        },
    },
    liveData: {
        title: 'Live Data',
        description: <>Stream real measurements from a commissioned site back into the same diagram you designed.</>,
        icon: <TbBroadcast strokeWidth={1.25} />,
        // action: {
        //     label: 'Learn more',
        //     href: '/design-software/features/live-data',
        // },
        preview: {
            type: 'video',
            sources: [
                { src: URLHelpers.file('/videos/features/live-data.webm'), type: 'video/webm' },
                { src: URLHelpers.file('/videos/features/live-data.mp4'), type: 'video/mp4' },
            ],
        },
    },
    powerFlowSimulation: {
        title: 'Power Flow Simulation',
        description: <>Simulate how your system behaves under different conditions and optimize your design.</>,
        icon: <TbArrowsDoubleNeSw strokeWidth={1.25} />,
        // action: {
        //     label: 'Learn more',
        //     href: '/design-software/features/power-flow-simulation',
        // },
        preview: {
            type: 'video',
            sources: [
                { src: URLHelpers.file('/videos/features/power-flow-simulation.webm'), type: 'video/webm' },
                { src: URLHelpers.file('/videos/features/power-flow-simulation.mp4'), type: 'video/mp4' },
            ],
        },
        gradient: 'blue',
    },
    productCatalog: {
        title: 'Product Catalog',
        description: (
            <>
                Find the products you need that fit your design. Search by product type, manufacturer, or
                specifications.
            </>
        ),
        icon: <TbListSearch strokeWidth={1.25} />,
        // action: {
        //     label: 'Learn more',
        //     href: '/design-software/features/product-catalog',
        // },
        preview: {
            type: 'video',
            sources: [
                { src: URLHelpers.file('/videos/features/search-products.webm'), type: 'video/webm' },
                { src: URLHelpers.file('/videos/features/search-products.mp4'), type: 'video/mp4' },
            ],
        },
        gradient: 'pink',
    },
    prototyping: {
        title: 'Prototype in Minutes',
        description: <>Sketch complete concepts in minutes by dragging and dropping your components.</>,
        icon: <TbClock strokeWidth={1.25} />,
        action: {
            label: 'Learn more',
            href: '/design-software/features/prototyping',
        },
        preview: {
            type: 'video',
            sources: [
                { src: URLHelpers.file('/videos/features/prototyping/drag-and-drop.webm'), type: 'video/webm' },
                { src: URLHelpers.file('/videos/features/prototyping/drag-and-drop.mp4'), type: 'video/mp4' },
            ],
        },
        gradient: 'red',
    },
    realtimeCollaboration: {
        title: 'Realtime Collaboration',
        description: (
            <>Collaborate with your team, manufacturers and clients. Everyone sees the same design, in real-time.</>
        ),
        icon: <TbPointer strokeWidth={1.25} />,
        // action: {
        //     label: 'Learn more',
        //     href: '/design-software/features/realtime-collaboration',
        // },
        preview: {
            type: 'video',
            sources: [
                { src: URLHelpers.file('/videos/features/sharing-sidebar.webm'), type: 'video/webm' },
                { src: URLHelpers.file('/videos/features/sharing-sidebar.mp4'), type: 'video/mp4' },
            ],
        },
        gradient: 'blue',
    },
    validations: {
        title: 'Validation Center',
        description: <>Automatically validate your design against industry standards and best practices.</>,
        icon: <TbShieldCheck strokeWidth={1.25} />,
        // action: {
        //     label: 'Learn more',
        //     href: '/design-software/features/validations',
        // },
        preview: {
            type: 'scrolling-image',
            image: validationsImage,
        },
        gradient: 'green',
    },
    wireSizing: {
        title: 'Wire Sizing',
        description: (
            <>Let our wire sizing engine suggest or validate your wire sizes according to IEC and NEC standards.</>
        ),
        icon: <TbMathMaxMin strokeWidth={1.25} />,
        // action: {
        //     label: 'Learn more',
        //     href: '/design-software/features/wire-sizing',
        // },
        gradient: 'purple',
        badge: 'Beta',
        preview: {
            type: 'scrolling-image',
            image: wireSizingsImage,
        },
    },
} as const;

export { features };
